import { StrictMode, Suspense, lazy } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, useLocation } from "react-router-dom";
import { App } from "./App.js";
import { AuthProvider } from "./auth.js";
import { UsageAnalytics } from "./analytics/UsageAnalytics.js";
import { BottomOverlayContract } from "./BottomOverlayContract.js";
import { InstallPromotion } from "./InstallPromotion.js";
import { PwaUpdatePrompt } from "./PwaUpdatePrompt.js";
import "./ui/tokens.css";
import "./ui/base.css";
import "./ui/shell.css";
import "./ui/layout-base.css";
import "./ui/form-fields-base.css";
import "./ui/control-base.css";
import "./styles.css";
import "./students.css";
import "./calendar/diaryPresentation.css";
import "./dashboard.css";
import "./operations.css";
import "./scheduleManagement.css";
import "./adminSettings.css";
import "./paymentsReports.css";
// Preserve the former Groove sheets' cascade slot under their shared feature owner.
import "./pages/account.css";
import "./studentImport.css";
import "./studentRecord.css";
// Keep the family shell sheet at its original cascade slot so its transition reset beats the staff shell rules.
import "./familyShell.css";
import "./ui/shellNavigation.css";
import "./studentAccessibility.css";
import "./staffWorkspace.css";
import "./grooveAdapter.css";
import "./ui/dialog.css";
import "./ui/geometry.css";
import "./ui/forms.css";
import "./ui/ux.css";
import "./ui/layout.css";
import "./calendar/interactions.css";
import "./calendar/blockedTime.css";
import "./brand.css";
import "./responsiveFollowup.css";
import "./calendar/diaryResponsive.css";
import "./installPromotion.css";
import "./pwaUpdatePrompt.css";

/**
 * A drummer's expected desktop transport shortcut.
 *
 * Kept at the application boundary because both the staff editor and the
 * public/student player use it. Form controls deliberately keep Space for
 * themselves, so changing a title, selecting an option or typing a lesson note
 * can never start playback by surprise.
 */
function formOwnsSpace(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  return target.matches("input, textarea, select, button, [role='textbox']");
}

/**
 * Prefer the explicit transport marker used by the public player. The staff
 * editor predates that marker, so while it is mounted we identify its transport
 * by the control's semantic label rather than by its position in `.groove-actions`.
 * Once found, mark it too. Adding another action before Play can therefore never
 * silently steal the Spacebar shortcut.
 */
function grooveTransport(): HTMLButtonElement | null {
  const marked = document.querySelector<HTMLButtonElement>("[data-groove-transport]");
  if (marked) return marked;

  const editorTransport = [...document.querySelectorAll<HTMLButtonElement>(".groove-editor .groove-actions button")]
    .find((button) => {
      const label = button.textContent?.trim();
      return label === "Play" || label === "Stop";
    });
  if (!editorTransport) return null;
  editorTransport.dataset.grooveTransport = "";
  return editorTransport;
}

document.addEventListener("keydown", (event) => {
  if (event.code !== "Space" || event.repeat || formOwnsSpace(event.target)) return;

  const transport = grooveTransport();
  if (!transport || transport.disabled || transport.getClientRects().length === 0) return;

  event.preventDefault();
  transport.click();
});

const container = document.getElementById("root");
if (!container) throw new Error("No #root element");

const CaldayRoom = lazy(() => import("./calday-room/CaldayRoom.js"));

function HubApplication() {
  const { pathname } = useLocation();
  // A separate staff calendar, even for visitors without a Hub account. It
  // neither bootstraps Hub identity nor mounts Hub analytics or install prompts.
  if (pathname === "/calday-room" || pathname === "/calday-room/") {
    return <Suspense fallback={null}><CaldayRoom/></Suspense>;
  }
  return <AuthProvider>
    <App />
    {pathname !== "/calday-room/manage" && <>
      <UsageAnalytics />
      <InstallPromotion />
      <PwaUpdatePrompt />
      <BottomOverlayContract />
    </>}
  </AuthProvider>;
}

createRoot(container).render(
  <StrictMode>
    <BrowserRouter>
      <HubApplication />
    </BrowserRouter>
  </StrictMode>,
);
